/**
 * ExternalSourceInfoPanel — panel ITE „Globalne słowniki" (Etap 10.5, ADR-0066 §7/§11).
 *
 * Read-only widok metadanych zarejestrowanego źródła zewnętrznego: nazwa, opis i lista
 * zadeklarowanych parametrów (nazwa, rodzaj, wymagany/opcjonalny). Panel ITE NIGDY nie widzi
 * realnych pozycji słownika zewnętrznego (dane pochodzą z bazy tenanta, do której admin
 * platformy nie ma wglądu) — stąd zawsze dołączona nota `externalNoPreview`.
 *
 * Reużywany w dwóch miejscach: `GlobalDictionaryFormModal` (podgląd przy wyborze źródła
 * podczas tworzenia/edycji) i `AdminGlobalDictionaries` (zamiast tabeli pozycji, gdy
 * wybrany słownik ma `sourceKind==='external'`).
 */

import { useTranslation } from 'react-i18next';

import type {
  DictionarySourceMeta,
  DictionarySourceParamType,
} from '@/lib/global-dictionaries/api';

/** Etykiety TYPÓW parametru (etap 1, Faza 2b ADR-0068) — deklaracja nie niesie już `kind`. */
const PARAM_TYPE_I18N_KEY: Record<DictionarySourceParamType, string> = {
  text: 'admin.globalDictionaries.form.paramType.text',
  int: 'admin.globalDictionaries.form.paramType.int',
  decimal: 'admin.globalDictionaries.form.paramType.decimal',
  date: 'admin.globalDictionaries.form.paramType.date',
  bool: 'admin.globalDictionaries.form.paramType.bool',
};

export interface ExternalSourceInfoPanelProps {
  /**
   * Metadane źródła z rejestru (`GET /api/admin/dictionary-sources`). `null` gdy jeszcze
   * nic nie wybrano (modal, przed pickerem) albo gdy rejestr zdryfował od utworzenia
   * słownika (wpis usunięty z kodu) — w obu przypadkach degradujemy do samego `sourceKey`.
   */
  source: DictionarySourceMeta | null;
  /** Klucz źródła zapisany na słowniku — fallback do wyświetlenia, gdy `source` jest `null`. */
  sourceKey?: string | null;
}

export function ExternalSourceInfoPanel({ source, sourceKey }: ExternalSourceInfoPanelProps) {
  const { t } = useTranslation();

  return (
    <div
      className="space-y-3 rounded-lg border border-border-primary bg-bg-secondary p-3"
      data-testid="external-source-info-panel"
    >
      <div>
        <p className="text-sm font-semibold text-text-primary">
          {source ? t(source.nameI18nKey) : (sourceKey ?? '')}
        </p>
        {source && (
          <p className="mt-1 text-xs text-text-tertiary">{t(source.descriptionI18nKey)}</p>
        )}
      </div>

      {source && source.params.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t('admin.globalDictionaries.form.sourceParamsTitle')}
          </p>
          <ul className="mt-1.5 space-y-1" data-testid="external-source-info-params">
            {source.params.map((param) => (
              <li
                key={param.name}
                className="flex items-center justify-between gap-2 text-sm text-text-secondary"
              >
                <span>
                  <code className="rounded bg-bg-tertiary px-1 py-0.5 text-xs">{param.name}</code> —{' '}
                  {t(PARAM_TYPE_I18N_KEY[param.type])}
                </span>
                <span className="shrink-0 text-xs text-text-muted">
                  {param.required
                    ? t('admin.globalDictionaries.form.paramRequired')
                    : t('admin.globalDictionaries.form.paramOptional')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs italic text-text-tertiary">
        {t('admin.globalDictionaries.form.externalNoPreview')}
      </p>
    </div>
  );
}
